import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps } from "@lib/types/Api";
import { Provider } from "@lib/types/Provider";
import {
  Button,
  Divider,
  Form,
  Image,
  Input,
  Space,
  Switch,
  Upload,
  UploadFile,
  UploadProps,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import ProviderFormItems from "../ProviderFormItems";
import {
  fileToBase64,
  generateLogoFromText,
  urlToBase64,
} from "@lib/helpers/FileHelper";
import { providers } from "@lib/constants/Provider";
import { providerSchema } from "@lib/schemas/provider";
import { createYupSync } from "@lib/helpers/TableHelper";
import { BsCloudUpload } from "react-icons/bs";
import { useApi } from "@lib/hooks/APIHooks";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<Partial<Provider>>;
  provider?: Provider;
};

export default function ProviderForm({
  openModal,
  toggleModal,
  mutationQuery,
  provider,
}: Props) {
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const providerName = Form.useWatch("name", form);
  const { mutate, isPending } = mutationQuery;

  const { queriesCacheClear } = useApi();

  const initialValues = useMemo(() => {
    return {
      enabled: true,
      ...provider,
      config: {
        ...provider?.config,
      },
    } as Provider;
  }, [provider]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
  }, [initialValues]);

  const handleClose = useCallback(() => {
    handleResetForm();
    toggleModal(false);
    form?.resetFields();
  }, [toggleModal, form]);

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  useEffect(() => {
    if (!openModal) return;

    handleResetForm();

    if (provider?.logo) {
      setFileList([
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: provider?.logo,
          response: provider?.logo,
        },
      ]);
    } else {
      setFileList([]);
    }
  }, [openModal, handleResetForm]);

  const onChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    if (newFileList.length > 0) {
      setFileList([newFileList[newFileList.length - 1]]);
    } else {
      setFileList([]);
    }
  };

  const [previewImg, setPreviewImg] = useState<string>();
  const onPreview = async (file: UploadFile) => {
    let src = file.url as string;
    if (!src) {
      src = await fileToBase64(file.originFileObj as File);
    }
    setPreviewImg(src);
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleGenerateLogo = useCallback(() => {
    if (!providerName || providerName.trim().length === 0) {
      return;
    }

    const generatedLogo = generateLogoFromText(providerName);
    if (generatedLogo) {
      form.setFieldValue("logo", generatedLogo);
      setFileList([
        {
          uid: "-1",
          name: "generated-logo.png",
          status: "done",
          url: generatedLogo,
          response: generatedLogo,
        },
      ]);
    }
  }, [providerName, form]);

  return (
    <Form<Provider>
      {...layout}
      form={form}
      name={"provider-modal"}
      onFinish={async (values) => {
        let base64Logo = "";

        if (
          values.logo &&
          typeof values.logo === "string" &&
          values.logo.startsWith("data:image/")
        ) {
          base64Logo = values.logo;
        } else if (
          provider?.logo &&
          typeof provider.logo === "string" &&
          provider.logo.startsWith("data:image/")
        ) {
          base64Logo = provider.logo;
        } else {
          const logo = providers.find(
            (item) => item.name === provider?.name,
          )?.logo;
          if (logo) {
            base64Logo = await urlToBase64(logo);
          }
        }

        mutate({
          id: provider?.id,
          ...values,
          logo: base64Logo,
          config: {
            ...provider?.config,
            ...values.config,
          },
        } as Partial<Provider>);

        queriesCacheClear({ searchKeys: ["providers"] });
      }}
      style={{ maxWidth: 600, marginTop: 20 }}
      autoComplete="off"
      initialValues={initialValues}
    >
      <Form.Item
        label={messages["common.logo"] as string}
        valuePropName="fileList"
        getValueFromEvent={normFile}
        name="logo"
        rules={[createYupSync({ schema: providerSchema })]}
      >
        <Input type="hidden" />
        {/* <ImgCrop rotationSlider> */}
        <Upload
          listType="picture-card"
          multiple={false}
          onChange={onChange}
          onPreview={onPreview}
          fileList={fileList}
          customRequest={async (props) => {
            const image = await fileToBase64(props.file as File);
            form.setFieldValue("logo", image);
            props.onSuccess(image);
          }}
        >
          <button style={{ border: 0, background: "none" }} type="button">
            <BsCloudUpload />
            <div style={{ marginTop: 8 }}>
              {messages["common.upload"] as string}
            </div>
          </button>
        </Upload>
        {/* </ImgCrop> */}

        <Image
          width={200}
          style={{ display: "none" }}
          src=""
          preview={{
            visible: !!previewImg,
            src: previewImg,
            onVisibleChange: () => {
              setPreviewImg("");
            },
          }}
        />
        <div style={{ marginTop: 8 }}>
          <Button
            type="default"
            onClick={handleGenerateLogo}
            disabled={!providerName || providerName.trim().length === 0}
          >
            {(messages["common.generateLogo"] as string) || "Generate Logo"}
          </Button>
        </div>
      </Form.Item>
      <Divider type="vertical" />

      <Form.Item
        name="name"
        label={messages["common.name"] as string}
        rules={[createYupSync({ schema: providerSchema })]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="enabled"
        label={messages["common.enabled"] as string}
        valuePropName="checked"
        rules={[createYupSync({ schema: providerSchema })]}
      >
        <Switch
          checked={initialValues.enabled}
          checkedChildren={messages["common.enabled"] as string}
          unCheckedChildren={messages["common.disabled"] as string}
        />
      </Form.Item>

      <Divider type="vertical" />

      <ProviderFormItems objKey="config" provider={provider} />

      <StyledFlex style={{ justifyContent: "end" }}>
        <Space>
          <Button htmlType="button" onClick={handleClose} disabled={isPending}>
            {messages["common.reset"] as string}
          </Button>
          <Button type="primary" htmlType="submit" loading={isPending}>
            {provider?.id
              ? (messages["common.update"] as string)
              : (messages["common.create"] as string)}
          </Button>
        </Space>
      </StyledFlex>
    </Form>
  );
}
